import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Ticket, Calendar, MapPin, ArrowRight, QrCode, Download, Loader2, X, CalendarPlus } from 'lucide-react';
import { toast } from 'sonner';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyTicketsPage = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrModal, setQrModal] = useState(null); // { ticketId, dataUri, eventTitle, seatNumber }
  const [qrLoading, setQrLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    const fetchTickets = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/tickets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTickets(response.data);
      } catch (error) {
        console.error('Error fetching tickets:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchTickets();
  }, [user, token, authLoading, navigate]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).toUpperCase();
  };

  const handleShowQr = async (ticket) => {
    setQrLoading(true);
    try {
      const res = await axios.get(`${API}/tickets/${ticket.id}/qr`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQrModal({
        ticketId: ticket.id,
        dataUri: res.data.qr_data_uri,
        eventTitle: ticket.event_title,
        seatNumber: ticket.seat_number,
        tierName: ticket.tier_name,
        attendeeName: ticket.attendee_name || user?.name,
      });
    } catch (err) {
      toast.error('Failed to load QR code');
    } finally {
      setQrLoading(false);
    }
  };

  const handleDownloadPdf = async (ticket) => {
    setDownloadingId(ticket.id);
    try {
      const res = await axios.get(`${API}/tickets/${ticket.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `unight-ticket-${ticket.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Ticket PDF downloaded');
    } catch (err) {
      toast.error('Failed to download ticket');
    } finally {
      setDownloadingId(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="loading">
        <div className="text-[#B5FF00] font-mono uppercase tracking-widest animate-pulse">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16" data-testid="my-tickets-page">
      <Seo title="My Tickets" description="All your Unight tickets and RSVPs in one place — download QR codes, view event details, and manage bookings." path="/my-tickets" noindex />
      <div className="container mx-auto px-4 md:px-8">
        <div className="mb-12">
          <span className="font-mono text-xs tracking-widest text-[#B5FF00] mb-2 block">
            YOUR COLLECTION
          </span>
          <h1 className="font-['Josefin_Sans'] text-5xl md:text-6xl lg:text-7xl font-bold text-white">
            MY TICKETS
          </h1>
        </div>

        {tickets.length === 0 ? (
          <div className="text-center py-16 border border-zinc-800 bg-[#0A0A0A]">
            <Ticket size={48} className="text-zinc-700 mx-auto mb-4" />
            <h2 className="font-['Josefin_Sans'] text-2xl text-white mb-2">NO TICKETS YET</h2>
            <p className="text-zinc-500 mb-6">Your purchased tickets will appear here</p>
            <Link to="/" className="btn-primary inline-flex items-center gap-2">
              EXPLORE EVENTS <ArrowRight size={18} />
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="tickets-list">
            {tickets.map((ticket, index) => (
              <div
                key={ticket.id}
                className="bg-[#0A0A0A] border border-zinc-800 hover:border-zinc-700 transition-colors opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
                data-testid={`ticket-${ticket.id}`}
              >
                <div className="p-6 md:p-8">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`font-mono text-xs tracking-widest px-2 py-1 ${
                          ticket.status === 'confirmed'
                            ? 'text-[#B5FF00] bg-[#B5FF00]/10'
                            : 'text-zinc-400 bg-zinc-800'
                        }`}>
                          {ticket.status.toUpperCase()}
                        </span>
                        {ticket.seat_number && (
                          <span className="font-mono text-xs tracking-widest px-2 py-1 bg-zinc-800 text-zinc-400">
                            SEAT {ticket.seat_number}
                          </span>
                        )}
                      </div>

                      <h3 className="font-['Josefin_Sans'] text-2xl md:text-3xl text-white mb-3">
                        {ticket.event_title}
                      </h3>

                      <div className="flex flex-wrap gap-4 text-zinc-400 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar size={16} strokeWidth={1.5} className="text-[#B5FF00]" />
                          <span className="font-mono">
                            {formatDate(ticket.event_date)}
                            {ticket.event_date_label && (
                              <span className="ml-2 px-1.5 py-0.5 text-[10px] font-mono bg-[#B5FF00]/10 text-[#B5FF00] border border-[#B5FF00]/30 tracking-wider align-middle">
                                {ticket.event_date_label.toUpperCase()}
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin size={16} strokeWidth={1.5} className="text-[#B5FF00]" />
                          <span className="font-mono">{ticket.event_venue}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col md:items-end gap-2">
                      <div className="bg-[#050505] border border-zinc-800 px-4 py-3 text-center">
                        <p className="font-mono text-xs text-zinc-500 mb-1">TICKET TYPE</p>
                        <p className="font-['Josefin_Sans'] text-lg text-white">{ticket.tier_name}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-mono text-xs text-zinc-500">PRICE</p>
                          <p className="font-mono text-[#B5FF00]">₹{ticket.total_price.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-zinc-800 px-6 md:px-8 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <span className="font-mono text-xs text-zinc-600">
                    PURCHASED: {new Date(ticket.purchased_at).toLocaleDateString('en-GB')}
                  </span>
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <button
                      onClick={() => handleShowQr(ticket)}
                      disabled={qrLoading}
                      className="font-mono text-xs text-white border border-zinc-700 hover:border-[#B5FF00] hover:text-[#B5FF00] transition-colors px-3 py-2 flex items-center gap-2 flex-1 sm:flex-none justify-center"
                      data-testid={`show-qr-${ticket.id}`}
                    >
                      <QrCode size={14} />
                      SHOW QR
                    </button>
                    <button
                      onClick={() => handleDownloadPdf(ticket)}
                      disabled={downloadingId === ticket.id}
                      className="font-mono text-xs text-[#050505] bg-[#B5FF00] hover:bg-[#a3e600] transition-colors px-3 py-2 flex items-center gap-2 flex-1 sm:flex-none justify-center"
                      data-testid={`download-pdf-${ticket.id}`}
                    >
                      {downloadingId === ticket.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Download size={14} />
                      )}
                      DOWNLOAD PDF
                    </button>
                    <button
                      onClick={async () => {
                        // Fetch the .ics behind auth, then trigger a browser
                        // download. iOS/Android natively open .ics as
                        // "Add to Calendar" so no extra JS is needed.
                        try {
                          const res = await axios.get(
                            `${API}/tickets/${ticket.id}/calendar.ics`,
                            {
                              headers: { Authorization: `Bearer ${token}` },
                              responseType: 'blob',
                            }
                          );
                          const blob = new Blob([res.data], { type: 'text/calendar' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `unight-${ticket.id.slice(0, 8)}.ics`;
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                        } catch (e) {
                          console.error('ICS download failed', e);
                        }
                      }}
                      className="font-mono text-xs text-white border border-zinc-800 hover:border-[#B5FF00] hover:text-[#B5FF00] transition-colors px-3 py-2 flex items-center gap-2 flex-1 sm:flex-none justify-center"
                      data-testid={`add-to-calendar-${ticket.id}`}
                    >
                      <CalendarPlus size={14} />
                      ADD TO CALENDAR
                    </button>
                    <Link
                      to={`/events/${ticket.event_id}`}
                      className="font-mono text-xs text-zinc-500 hover:text-white flex items-center gap-1"
                    >
                      EVENT <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* QR Modal */}
      {qrModal && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setQrModal(null)}
          data-testid="qr-modal"
        >
          <div
            className="bg-[#0A0A0A] border border-zinc-800 max-w-sm w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setQrModal(null)}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white"
              data-testid="close-qr-modal"
            >
              <X size={20} />
            </button>

            <div className="text-center">
              <div className="font-mono text-xs tracking-widest text-[#B5FF00] mb-2">UNIGHT TICKET</div>
              <h3 className="font-['Josefin_Sans'] text-2xl text-white mb-1">{qrModal.eventTitle}</h3>
              <p className="text-zinc-500 text-sm mb-6">{qrModal.tierName}{qrModal.seatNumber ? ` · Seat ${qrModal.seatNumber}` : ''}</p>

              <div className="bg-white p-4 inline-block mb-4">
                <img
                  src={qrModal.dataUri}
                  alt="Ticket QR code"
                  className="w-64 h-64 block"
                  data-testid="qr-image"
                />
              </div>

              <p className="font-mono text-xs text-zinc-500 mb-2">
                {qrModal.attendeeName?.toUpperCase()}
              </p>
              <p className="font-mono text-xs text-zinc-600">
                TICKET ID · {qrModal.ticketId.slice(0, 8).toUpperCase()}
              </p>
              <p className="text-zinc-500 text-xs mt-6 italic">
                Single entry — show this QR at the door. Non-transferable.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTicketsPage;
